import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, Eye, Tag, Share2 } from 'lucide-react';
import DOMPurify from 'dompurify';
import toast from 'react-hot-toast';
import axiosInstance from '../lib/axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function BlogArticle() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchArticle();
  }, [slug]);

  const fetchArticle = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`${API}/blog/articles/${slug}`);
      setArticle(response.data);
      
      // Update page title and meta description
      if (response.data) {
        document.title = `${response.data.title} | Achat Immo Gagnant`;
        if (response.data.meta_description) {
          const metaDesc = document.querySelector('meta[name="description"]');
          if (metaDesc) {
            metaDesc.setAttribute('content', response.data.meta_description);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching article:', error);
      if (error.response?.status === 404) {
        toast.error('Article introuvable');
        navigate('/blog');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleShare = async () => {
    const url = window.location.href;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: article.title,
          text: article.meta_description || article.excerpt,
          url: url
        });
      } catch (error) {
        if (error.name !== 'AbortError') {
          copyToClipboard(url);
        }
      }
    } else {
      copyToClipboard(url);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Lien copié dans le presse-papier !');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Chargement de l'article...</p>
        </div>
      </div>
    );
  }

  if (!article) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <Link to="/" className="text-2xl font-bold text-blue-600">
              Achat Immo Gagnant
            </Link>
            <nav className="flex gap-6">
              <Link to="/" className="text-slate-600 hover:text-blue-600 transition">
                Accueil
              </Link>
              <Link to="/simulation/wizard" className="text-slate-600 hover:text-blue-600 transition">
                Simulation
              </Link>
              <Link to="/blog" className="text-slate-600 hover:text-blue-600 transition">
                Blog
              </Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Article Content */}
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Back Button */}
        <Link
          to="/blog"
          className="inline-flex items-center gap-2 text-slate-600 hover:text-blue-600 transition mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Retour au blog</span>
        </Link>

        {/* Featured Image */}
        {article.featured_image_url && (
          <img
            src={article.featured_image_url}
            alt={article.title}
            className="w-full h-96 object-cover rounded-2xl shadow-lg mb-8"
          />
        )}

        {/* Article Header */}
        <header className="mb-8">
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4">
            {article.title}
          </h1>

          {/* Meta Info */}
          <div className="flex flex-wrap items-center gap-6 text-slate-600 mb-6">
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              <span>{formatDate(article.published_at)}</span>
            </div>
            <div className="flex items-center gap-2">
              <Eye className="w-5 h-5" />
              <span>{article.views || 0} vues</span>
            </div>
            <button
              onClick={handleShare}
              className="flex items-center gap-2 hover:text-blue-600 transition"
            >
              <Share2 className="w-5 h-5" />
              <span>Partager</span>
            </button>
          </div>

          {/* Tags */}
          {article.tags && article.tags.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <Tag className="w-4 h-4 text-slate-500" />
              {article.tags.map((tag, index) => (
                <span
                  key={index}
                  className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}
        </header>

        {/* Article Content */}
        <div
          className="prose prose-lg max-w-none prose-headings:text-slate-900 prose-p:text-slate-700 prose-a:text-blue-600 prose-strong:text-slate-900 prose-img:rounded-xl"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(article.content_html) }}
        />

        {/* Footer CTA */}
        <div className="mt-12 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 text-white text-center">
          <h2 className="text-2xl font-bold mb-4">
            Prêt à analyser votre investissement immobilier ?
          </h2>
          <p className="text-blue-100 mb-6 max-w-2xl mx-auto">
            Utilisez notre outil de simulation pour obtenir une analyse complète de la rentabilité de votre projet immobilier en quelques minutes.
          </p>
          <Link
            to="/simulation/wizard"
            className="inline-block bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-blue-50 transition"
          >
            Commencer ma simulation gratuite
          </Link>
        </div>
      </article>
    </div>
  );
}
